import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Calculator, MapPin, ArrowRight } from "lucide-react";
import { useI18n } from "../context/I18nContext";

/* Pickup & Delivery cost calculator
 *  Lokaal ≤ 25 km vanaf Rotterdam: vast tarief per volumeklasse.
 *  Landelijk > 25 km: basistarief + (km × 2) × € 0,44.
 */

const LOCAL_RADIUS_KM = 25;
const KM_RATE = 0.44;

const VOLUME_KEYS = {
  TO_HALF: "tot_0_5",
  HALF_TO_ONE: "0_5_tot_1",
  FROM_ONE: "vanaf_1",
};

const VOLUME_OPTIONS = [
  { key: VOLUME_KEYS.TO_HALF,     i18n: "vol_to_half",     local: 19.99, national: 25.99 },
  { key: VOLUME_KEYS.HALF_TO_ONE, i18n: "vol_half_to_one", local: 34.99, national: 44.99 },
  { key: VOLUME_KEYS.FROM_ONE,    i18n: "vol_from_one",    local: 49.99, national: 64.99 },
];

function fmt(n) {
  return `€ ${Number(n).toFixed(2).replace(".", ",")}`;
}

export default function PickupDeliveryCalculator() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [distance, setDistance] = useState("");
  const [volume, setVolume] = useState(VOLUME_KEYS.TO_HALF);
  const [city, setCity] = useState("");
  const [postcode, setPostcode] = useState("");
  const [boxes, setBoxes] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  const calc = useMemo(() => {
    if (distance === "" || distance === null) return null;
    const km = Number(distance);
    if (!Number.isFinite(km) || km < 0) {
      return { error: t("pickup_calc.negative_error") };
    }
    const tier = VOLUME_OPTIONS.find((v) => v.key === volume);
    if (!tier) return null;
    const tierLabel = t(`pickup_calc.${tier.i18n}`);
    if (km <= LOCAL_RADIUS_KM) {
      return {
        type: t("pickup_calc.local"),
        base: tier.local,
        kmCost: 0,
        total: tier.local,
        tierLabel,
        kmRate: 0,
        km,
        returnKm: 0,
      };
    }
    const returnKm = km * 2;
    const kmCost = Math.round(returnKm * KM_RATE * 100) / 100;
    return {
      type: t("pickup_calc.national"),
      base: tier.national,
      kmCost,
      total: Math.round((tier.national + kmCost) * 100) / 100,
      tierLabel,
      kmRate: KM_RATE,
      km,
      returnKm,
    };
  }, [distance, volume, t]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (distance === "" || Number(distance) < 0) {
      setError(t("pickup_calc.negative_error"));
      return;
    }
    setError("");
  };

  const goBooking = () => {
    const tier = VOLUME_OPTIONS.find((v) => v.key === volume);
    const tierLabel = tier ? t(`pickup_calc.${tier.i18n}`) : "";
    const fragments = [
      `Pickup calculator: ${calc?.type || ""} (${calc?.km ?? 0} km, ${tierLabel})`,
      calc ? `${t("pickup_calc.result_indication")}: ${fmt(calc.total)}` : null,
      city ? `${t("pickup_calc.city_label")}: ${city}` : null,
      postcode ? `${t("pickup_calc.postcode_label")}: ${postcode}` : null,
      boxes ? `${t("pickup_calc.boxes_label")}: ${boxes}` : null,
      note ? `${t("pickup_calc.note_label")}: ${note}` : null,
    ].filter(Boolean);
    const summary = fragments.join(" — ");
    navigate(`/boekingsformulier?note=${encodeURIComponent(summary)}`);
  };

  return (
    <section className="py-20 bg-gradient-to-b from-slate-50 via-white to-slate-50" data-testid="pickup-calculator">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="eyebrow inline-flex items-center gap-1.5">
          <Calculator className="w-3.5 h-3.5" /> {t("pickup_calc.title")}
        </span>
        <h2 className="font-chivo font-black text-3xl sm:text-4xl text-brand-900 tracking-tight mt-3">
          {t("pickup_calc.heading")}
        </h2>
        <p className="text-ink-500 mt-3 max-w-2xl text-[15px] leading-relaxed">
          {t("pickup_calc.intro")}
        </p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Inputs */}
          <form onSubmit={onSubmit} className="lg:col-span-2 card-premium p-6 sm:p-8 space-y-5" data-testid="pickup-calc-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="label-saas">{t("pickup_calc.distance_label")} *</Label>
                <Input
                  data-testid="pickup-calc-distance"
                  type="number"
                  step="0.1"
                  min="0"
                  value={distance}
                  onChange={(e) => { setDistance(e.target.value); setError(""); }}
                  className="rounded-xl"
                  placeholder="20"
                />
              </div>
              <div>
                <Label className="label-saas">{t("pickup_calc.volume_label")} *</Label>
                <select
                  data-testid="pickup-calc-volume"
                  value={volume}
                  onChange={(e) => setVolume(e.target.value)}
                  className="select-saas"
                >
                  {VOLUME_OPTIONS.map((v) => (
                    <option key={v.key} value={v.key}>{t(`pickup_calc.${v.i18n}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="label-saas">{t("pickup_calc.city_label")}</Label>
                <Input data-testid="pickup-calc-city" value={city} onChange={(e) => setCity(e.target.value)} className="rounded-xl" />
              </div>
              <div>
                <Label className="label-saas">{t("pickup_calc.postcode_label")}</Label>
                <Input data-testid="pickup-calc-postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} className="rounded-xl" />
              </div>
              <div>
                <Label className="label-saas">{t("pickup_calc.boxes_label")}</Label>
                <Input data-testid="pickup-calc-boxes" type="number" min="0" value={boxes} onChange={(e) => setBoxes(e.target.value)} className="rounded-xl" />
              </div>
              <div className="sm:col-span-2">
                <Label className="label-saas">{t("pickup_calc.note_label")}</Label>
                <Input data-testid="pickup-calc-note" value={note} onChange={(e) => setNote(e.target.value)} className="rounded-xl" placeholder={t("pickup_calc.note_placeholder")} />
              </div>
            </div>

            {error && (
              <div className="rounded-xl bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 text-sm" data-testid="pickup-calc-error">{error}</div>
            )}

            <div className="text-xs text-ink-400 space-y-1 pt-2 border-t border-slate-100">
              <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-brand-500" /> {t("pickup_calc.help_radius")}</div>
              <div>{t("pickup_calc.help_formula")}</div>
            </div>
          </form>

          {/* Result */}
          <aside className="card-dark p-7 lg:sticky lg:top-24 self-start relative overflow-hidden" data-testid="pickup-calc-result">
            <div className="absolute -top-20 -right-12 w-56 h-56 rounded-full bg-brand-500/30 blur-3xl pointer-events-none" />
            <div className="relative">
            {calc?.error ? (
              <div className="text-sm bg-red-500/30 border-l-4 border-red-400 px-3 py-2.5 rounded-lg" data-testid="pickup-calc-input-error">{calc.error}</div>
            ) : calc ? (
              <>
                <div className="text-[11px] uppercase tracking-[0.18em] text-brand-300 font-chivo font-bold">{t("pickup_calc.result_indication")}</div>
                <div className="font-chivo font-black text-5xl mt-3 font-mono-num" data-testid="pickup-calc-total">{fmt(calc.total)}</div>
                <div className="mt-4 inline-block text-[10px] uppercase tracking-wider font-chivo font-bold px-3 py-1.5 rounded-full bg-brand-500 text-white" data-testid="pickup-calc-type">
                  {t("pickup_calc.tariff_type")}: {calc.type}
                </div>
                <ul className="mt-5 text-xs text-white/85 space-y-1.5" data-testid="pickup-calc-breakdown">
                  <li>{t("pickup_calc.single_distance")}: <span className="font-mono-num font-bold text-white" data-testid="pickup-calc-single-km">{calc.km} km</span></li>
                  {calc.returnKm > 0 && (
                    <li>{t("pickup_calc.return_distance")}: <span className="font-mono-num font-bold text-white" data-testid="pickup-calc-return-km">{calc.returnKm} km</span></li>
                  )}
                  <li>{t("pickup_calc.base_rate")} ({calc.tierLabel}): <span className="font-mono-num font-bold text-white">{fmt(calc.base)}</span></li>
                  {calc.kmCost > 0 && (
                    <li>{t("pickup_calc.km_cost")} ({calc.returnKm} km × € 0,44): <span className="font-mono-num font-bold text-white">{fmt(calc.kmCost)}</span></li>
                  )}
                </ul>
                <Button
                  onClick={goBooking}
                  className="mt-7 w-full rounded-xl bg-white text-brand-900 hover:bg-brand-50 font-chivo font-bold uppercase text-sm shadow-brand-card transition-all"
                  data-testid="pickup-calc-cta"
                >
                  {t("pickup_calc.cta")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <p className="text-[11px] text-white/60 mt-4 leading-relaxed" data-testid="pickup-calc-disclaimer">
                  {t("pickup_calc.disclaimer")}
                </p>
              </>
            ) : (
              <div className="text-sm text-white/80" data-testid="pickup-calc-empty">
                {t("pickup_calc.empty_state")}
              </div>
            )}
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
